import classNames from "classnames/dedupe";
import React, { forwardRef, useMemo, useRef } from "react";
import { ITextInputProps } from "./TextInput.interface";
import useId from "../../../../hooks/useId";
import { TThemeVariant, useTheme } from "../../../Utility/ThemeProvider";
import TextInputEndAdornment from "./TextInputEndAdornment";
import noop from "../../../../helpers/noop";
import useValidation from "../hooks/useValidation";

const TextInput = forwardRef<HTMLInputElement, ITextInputProps>(
  (
    {
      label,
      description,
      value = undefined,
      defaultValue = undefined,
      placeholder,
      id,
      name,
      type = "text",
      required,
      autoComplete,
      disabled = false,
      errorMessage,
      validity = "none",
      dirty = false,
      alwaysShowPlaceholder = false,
      classes,
      variant,
      startAdornment,
      endAdornment,
      hideLeftIconOnFocus = "auto",
      hideRightIconOnFocus = "auto",
      onChange = noop,
      onBlur,
      onFocus,
      minValue = 0,
      validation = [],
      onEnter,
    },
    ref = null,
  ) => {
    /** Theming */
    const theme: TThemeVariant = useTheme(variant);

    const {
      inputValidity,
      currentDirty,
      inputValue,
      onInputChange,
      onKeyDownValidation,
      hasErrorMessage: showErrorMessage,
      validationClasses,
      floatingLineClasses: flLineClss,
      errorMessageClasses: errMssgClss,
      descriptionClasses: descriptnClss,
      handleBlur,
      handleFocus,
    } = useValidation({
      value: value ?? defaultValue ?? "",
      dirty,
      validation,
      validity,
      onChange,
      onFocus,
      onBlur,
      disabled,
      errorMessage,
      theme,
      minValue,
      type,
      onEnter,
    });

    /** make ref */
    const internalRef = useRef<HTMLInputElement>(null);
    const inputRef = ref || internalRef;

    /** Generate id for label */
    const [currentId] = useId({ id, name: "inputs" });

    const hideLeftIcon = useMemo(
      () => hideLeftIconOnFocus === "hide",
      [hideLeftIconOnFocus],
    );

    const hideRightIcon = useMemo(
      () =>
        hideRightIconOnFocus === "hide" ||
        (hideRightIconOnFocus === "auto" && inputValidity !== "none"),
      [hideRightIconOnFocus, inputValidity],
    );

    const inputClasses = classNames(
      "form-control",
      validationClasses,
      {
        "form-control--icon-left": startAdornment,
        "form-control--icon-right": endAdornment,
        "form-control--icon-left-focus-hide": hideLeftIcon,
        "form-control--icon-right-focus-hide": hideRightIcon,
        "form-control--show-placeholder": alwaysShowPlaceholder || disabled,
      },
      classes?.input,
    );

    const leftIconInjectedProps = useMemo(
      () => ({
        disabled,
        dirty: currentDirty,
        inputValidity,
        required,
        hideOnFocus: hideLeftIcon,
        classes: {
          root: classes?.leftIcon,
        },
      }),
      [classes, currentDirty, disabled, hideLeftIcon, required, inputValidity],
    );

    const rightIconInjectedProps = useMemo(
      () => ({
        disabled,
        dirty: currentDirty,
        inputValidity,
        required,
        hideOnFocus: hideRightIcon,
        classes: {
          root: classes?.rightIcon,
        },
      }),
      [classes, currentDirty, disabled, hideRightIcon, required, inputValidity],
    );

    const floatingLineClasses = classNames(flLineClss, classes?.floatingLine);

    const errorMessageClasses = classNames(errMssgClss, classes?.errorMessage);

    const descriptionClasses = classNames(descriptnClss, classes?.description);

    return (
      <div className={classNames("form-floating", classes?.root)}>
        <input
          ref={inputRef}
          id={currentId}
          data-testid="TextInput__input"
          type={type}
          min={type === "number" ? minValue : ""}
          name={name}
          value={inputValue}
          defaultValue={defaultValue}
          className={inputClasses}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          autoComplete={autoComplete}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onChange={onInputChange}
          onKeyDown={onKeyDownValidation}
        />
        {startAdornment?.(leftIconInjectedProps)}
        {label && !disabled ? (
          <label
            htmlFor={currentId}
            className={classNames(classes?.label)}
            data-testid="TextInput__label"
          >
            {label}
          </label>
        ) : null}
        <span className={floatingLineClasses} data-testid="TextInput__line" />
        {showErrorMessage && (
          <div
            className={errorMessageClasses}
            data-testid="TextInput__errorMessage"
          >
            {errorMessage}
          </div>
        )}
        {description ? (
          <div
            className={descriptionClasses}
            data-testid="TextInput__description"
          >
            {description}
          </div>
        ) : null}
        <TextInputEndAdornment
          disabled={disabled}
          adornment={endAdornment}
          injectedProps={rightIconInjectedProps}
          dirty={currentDirty}
          validity={inputValidity}
          classes={{
            root: classes?.rightIcon,
            successIcon: classes?.successIcon,
            errorIcon: classes?.errorIcon,
          }}
        />
      </div>
    );
  },
);

export default TextInput;
